import React, { useEffect, useRef } from 'react';
import { useListingPriceFairness } from '@/hooks/useListingPriceFairness';
import { gsap, ScrollTrigger } from '@/lib/gsap';

interface Props {
  listingId: string;
}

const LABELS: Record<'good' | 'fair' | 'high', { text: string; bar: string; dot: string }> = {
  good: { text: 'Sub media pieței', bar: 'bg-brand-green', dot: 'border-brand-green' },
  fair: { text: 'În media pieței', bar: 'bg-primary', dot: 'border-primary' },
  high: { text: 'Peste media pieței', bar: 'bg-brand-gold', dot: 'border-brand-gold' },
};

/**
 * Animated market-price bar: the fill and the price marker grow into place
 * when the block scrolls into view (GSAP ScrollTrigger).
 */
export const PriceBenchmarkBar: React.FC<Props> = ({ listingId }) => {
  const { data } = useListingPriceFairness(listingId);
  const rootRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);
  const markerRef = useRef<HTMLDivElement>(null);

  // Position of this listing on a -25% … +25% scale around the median.
  const median = data ? Number(data.median_price) : 0;
  const price = data ? Number(data.listing_price) : 0;
  const ratio = median > 0 ? (price - median) / median : 0;
  const pct = gsap.utils.clamp(3, 97, 50 + (ratio / 0.25) * 50);

  useEffect(() => {
    const root = rootRef.current;
    const fill = fillRef.current;
    const marker = markerRef.current;
    if (!root || !fill || !marker || !data || data.fairness_label === 'unknown') return;

    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      gsap.set(fill, { scaleX: 1 });
      gsap.set(marker, { left: `${pct}%`, opacity: 1 });
      return;
    }

    const ctx = gsap.context(() => {
      gsap.set(fill, { scaleX: 0, transformOrigin: 'left center' });
      gsap.set(marker, { left: '50%', opacity: 0, scale: 0.6 });

      const tl = gsap.timeline({
        scrollTrigger: { trigger: root, start: 'top 90%', once: true },
      });
      tl.to(fill, { scaleX: 1, duration: 0.8, ease: 'power3.out' })
        .to(marker, { opacity: 1, scale: 1, duration: 0.3 }, '-=0.45')
        .to(marker, { left: `${pct}%`, duration: 0.7, ease: 'power3.inOut' }, '<');

      ScrollTrigger.refresh();
    }, root);

    return () => ctx.revert();
  }, [data, pct]);

  if (!data || data.fairness_label === 'unknown') return null;
  const cfg = LABELS[data.fairness_label];

  return (
    <div ref={rootRef} className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-foreground">Poziția prețului în piață</span>
        <span className="text-xs text-muted-foreground">{data.sample_size} anunțuri comparabile</span>
      </div>

      <div className="relative mt-4 mb-6 h-2 rounded-full bg-muted overflow-visible">
        <div ref={fillRef} className={`absolute inset-0 rounded-full ${cfg.bar} opacity-25`} />
        {/* Median marker */}
        <div className="absolute top-1/2 left-1/2 h-4 w-px -translate-x-1/2 -translate-y-1/2 bg-border" />
        {/* Listing marker */}
        <div
          ref={markerRef}
          className={`absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-background shadow-sm ${cfg.dot}`}
          style={{ left: '50%' }}
        />
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>-25%</span>
        <span>Mediană {Math.round(median).toLocaleString('ro-RO')} €</span>
        <span>+25%</span>
      </div>

      <p className="mt-3 text-sm text-foreground">
        {cfg.text}
        {data.delta_pct !== null && (
          <span className="text-muted-foreground">
            {' '}
            ({data.delta_pct > 0 ? '+' : ''}
            {data.delta_pct}% față de mediană)
          </span>
        )}
      </p>
    </div>
  );
};
